import {CopyOutlined, DeleteFilled, MinusSquareOutlined, PlusOutlined, PlusSquareOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, message, Modal, Space, Spin, Table, Tag} from "antd";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {AiAttributeServiceContext, AppContextContext, VulSourceServiceContext, VulViewServiceContext} from "../../Contexts";
import VulSource from "../../domain/VulSource";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import * as styles from "./VulViewList.module.css";
import VulView, {isManagedVulView} from "../../domain/VulView";
import {ColumnsType} from "antd/es/table";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import ReadOnlyQueryView from "./ReadOnlyQueryView";
import {UserRole} from "../../domain/UserRole";
import QueryOptions from "../../sal-ui/QueryOptions";
import MarkdownContent from "../common/MarkdownContent";
import {GetComponentProps} from "rc-table/lib/interface";
import {Permission} from "../../domain/auth/Permission";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import AiAttribute from "../../domain/AiAttribute";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {FormPersister} from "../../sal-ui/FormPersister";
import _ from "lodash";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "VulViewList";
const FILTER_DEBOUNCE_MS = 500;

function VulViewList() {
    const appContext = useContext(AppContextContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);
    const vulViewService = useContext(VulViewServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [sources, setSources] = useState<VulSource[]>();
    const [aiAttributes, setAiAttributes] = useState<AiAttribute[]>();
    const [aiAttributesLoaded, setAiAttributesLoaded] = useState(false);
    const [views, setViews] = useState<PagedResult<VulView>>();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const title = "Vulnerability views";
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.VIEW__READ_SEAL),
        canReadOrganization: appContext.user.hasPermission(Permission.VIEW__READ_ORG)
    };

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setSources(value.data));
        aiAttributeService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(value => setAiAttributes(value.data))
            .catch(() => setAiAttributes([]))
            .finally(() => setAiAttributesLoaded(true));

        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    const columns: ColumnsType<VulView> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: (value, vulView) => <>
                <div>
                    <Link to={`/vul-source-items/views/${vulView.id}`}>{value}</Link>
                    {isManagedVulView(vulView) && <Tag style={{marginLeft: 8}} variant={"filled"} color={"blue"}>managed</Tag>}
                    {appContext.user.isSystemAdmin() && vulView.importedAt && <Tag style={{marginLeft: 8}} variant={"filled"} color={"blue"}>imported</Tag>}
                </div>

                {vulView.description && <SingleSummaryBlock><MarkdownContent children={vulView.description}/></SingleSummaryBlock>}

                {appContext.user.isSystemAdmin() && (vulView.tags?.length ?? 0) > 0 && (
                    <div style={{marginTop: 8}}>
                        {vulView.tags?.map(tag => <Tag key={`${vulView.id}-${tag}`}>{tag}</Tag>)}
                    </div>
                )}
            </>
        },
        {
            dataIndex: ["organization", "name"],
            key: "organization.name",
            title: "Owner",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            align: "left",
            render: renderOrganization
        }
    ];

    if (appContext.user.hasPermission(Permission.VIEW__MANAGE_ORG)) {
        columns.push(
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        );
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb
                    className={globalStyles["common__breadcrumb"]}
                    items={[
                        {title: appContext.config?.appName},
                        {title}
                    ]}
                />

                <h1>{title}</h1>

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {appContext.user.hasPermission(Permission.VIEW__MANAGE_ORG) &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/vul-source-items/views/add")}>Add</Button>
                    }

                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                    <Button
                        className={"btn-seamless"}
                        icon={<PlusSquareOutlined/>}
                        title={"Expand all"}
                        onClick={() => setExpandedRowKeys(views?.data?.map(value => value.id) ?? [])}
                    />
                    <Button
                        className={"btn-seamless"}
                        icon={<MinusSquareOutlined/>}
                        title={"Collapse all"}
                        onClick={() => setExpandedRowKeys([])}
                    />
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       dataSource={views?.data}
                       size="middle"
                       loading={tableHandler.loading}
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                       expandable={{
                           expandedRowKeys: expandedRowKeys,
                           onExpand: (expanded, record) => {
                               if (expanded) {
                                   setExpandedRowKeys(prevState => [...prevState, record.id]);
                               } else {
                                   setExpandedRowKeys(prevState => prevState.filter(v => v !== record.id));
                               }
                           },
                           expandedRowRender: record => (
                               <div className={styles.query}>
                                   <h2>Query</h2>

                                   {aiAttributesLoaded
                                       ? <ReadOnlyQueryView
                                           sources={sources}
                                           aiAttributes={aiAttributes}
                                           query={JSON.parse(record.query)}
                                       />
                                       : <Spin/>}
                               </div>
                           )
                       }}
                />
            </>
        </DocumentTitle>
    )

    function renderOrganization(value: any, vulView: VulView) {
        if (vulView.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(vulView.organization, value);
    }

    function renderAction(_: unknown, vulView: VulView) {
        return (
            <Space>
                <Button icon={<CopyOutlined/>} className={"ant-btn-icon-only"}
                        title={"Clone"}
                        onClick={() => navigate(`/vul-source-items/views/clone/${vulView.id}`)}/>

                {isEditableForPrincipal(vulView) &&
                    <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                            title={"Delete"}
                            onClick={() => {
                                Modal.confirm({
                                    content: <>Do you really want to delete view <b>{vulView.name}</b>?</>,
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    onOk: () => onDeleteConfirm(vulView)
                                });
                            }}/>
                }
            </Space>
        )
    }

    function isEditableForPrincipal(vulView: VulView) {
        if (isManagedVulView(vulView)) {
            return false;
        }

        if (vulView?.owner === ResourceOwnerType.SYSTEM && appContext.user.role !== UserRole.SYSTEM_ADMIN) {
            return false;
        }

        return true;
    }

    function onDeleteConfirm(vulView: VulView) {
        vulViewService.delete(vulView)
            .then(() => {
                message.success(<>View {vulView.name} successfully deleted.</>);

                tableHandler.reload();
            });
    }

    function onRow(vulView: VulView): ReturnType<GetComponentProps<VulView>> {
        return {onDoubleClick: () => navigate(`/vul-source-items/views/${vulView.id}`)};
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return vulViewService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setViews(value);
        });
    }
}

export default VulViewList;
